import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedSnackbar,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	IGetListRequest,
	IResponse,
	IServerError,
} from "../../models/common";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { IAppState } from "../../store/reducers";
import {
	INITIAL_SNACKBAR_DATA,
} from "../../common/constants";
import { RemittanceYearToDateFieldsLeftPanel } from "./Panels/RemittanceYearToDateFieldsLeftPanel";
import { RemittanceYearToDateFieldsRightPanel } from "./Panels/RemittanceYearToDateFieldsRightPanel";
import { RemittanceYearToDateSetupValidationSchema } from "./validator";
import { YearToDateService } from "../../services/YearToDateService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import {
	useNavigateToLogout,
} from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IRemittanceYearToDate { }
const SINGLE_RECORD_DATA_QUERY_KEY = "remittance-single-data-record";
const REMITTANCE_DATA_QUERY_KEY = "remittance-query-list";
const YEARS_DDL_LIST_QUERY_KEY = "years-ddl";

const useGetDataRemittanceYearToDateById = (
	id: string | undefined,
	// clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: () => {
			return YearToDateService.getDataRemittanceYearToDateById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetYearsDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [YEARS_DDL_LIST_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return YearToDateService.getYearsDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


export const RemittanceYearToDate: React.FC<IRemittanceYearToDate> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const [openDialog, setDialogOpen] = useState(false);
	const [isPendingData, setIsPendingData] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [remittanceYearToDateId, setRemittanceYearToDateId] = useState<any>(undefined);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedSplit, setSelectedSplit] = useState(200)
	const [selectedYear, setSelectedYear] = useState()
	const queryEmployee = useQueryClient();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const navigateToLogout = useNavigateToLogout();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "departmentName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending: isPendingPayFrequencyDDL, data: yearsDDLItems } =
		useGetYearsDDL(clientId, taxYear);


	const { isPending: isSubmitting, mutate: submitRemittanceYearToDateFormFields } =
		useMutation({
			mutationFn: YearToDateService.createRemittanceYearToDate,
			onSuccess: (res: IRemittanceYearToDate | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setEditRecordId(res.data.id);
					queryEmployee.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});
					queryEmployee.invalidateQueries({
						queryKey: [REMITTANCE_DATA_QUERY_KEY],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/settings/year-to-date/remittance/${res.data.id}`;
					navigate(path);
				} else if (res.status === 201) {
					setEditRecordId(res.data.id);
					queryEmployee.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});
					queryEmployee.invalidateQueries({
						queryKey: [REMITTANCE_DATA_QUERY_KEY],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/settings/year-to-date/remittance/${res.data.id}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const DEFAULT_VALUES = {
		year: undefined,
		remittedToCra: null,
		wsibRemitted: null,
		ehtRemitted: null,
		ehtBritishColumbia: null,
		taxesRemitted: null,
		hsfRemitted: null,
		csstRmeitted: null,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(RemittanceYearToDateSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const { data: selectedDataRecord } = useGetDataRemittanceYearToDateById(
		editRecordId,
		// clientId,
		navigateToLogout
	);



	const onSubmit = (values: any) => {

		const getValuess = formMethods.getValues()

		let payload = {
			clientId: clientId,
			// id: id,
			taxYear: values.year,
			remittedToCra: values.remittedToCra,
			wsibRemitted: values.wsibRemitted,
			ehtRemitted: values.ehtRemitted,
			// ehtBritishColumbia: values.ehtBritishColumbia,
			revenuQuebec: {
				taxesRemitted: values.taxesRemitted,
				hsfRemitted: values.hsfRemitted,
				csstRemitted: values.csstRemitted,
			}
		};

		submitRemittanceYearToDateFormFields({
			payload: payload,
		});
	};

	useEffect(() => {
		if (id && selectedDataRecord) {


			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				// if (fieldName === "percenagePerDollorInAcc1") {
				// 	formMethods.setValue(fieldName, fieldValue ?? undefined);
				// 	setSelectedSplit(fieldValue)
				// }
			});
		}
	}, [id && selectedDataRecord]);


	const onYearSelectionChangeHandler = (value: any) => {
		setSelectedYear(value)
	}
	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteRemittanceYearToDateById } = useMutation({
		mutationFn: YearToDateService.delRemittanceYearToDateById,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				queryEmployee.invalidateQueries({
					queryKey: [REMITTANCE_DATA_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/settings/year-to-date/remittance`;
			navigate(path);
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteEmployeeField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let payload: any = {
				remittanceYearToDateId: remittanceYearToDateId
			}
			deleteRemittanceYearToDateById(payload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setRemittanceYearToDateId(selectedDataRecord.id);
		}
	};


	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/settings/year-to-date/remittance`;
		navigate(path);
	};

	const expandedLabels = ["Settings", "Year To Date"];
	const expandedHomeRemittanceSetupOrSetupItem = "Remittance"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			if (clientId && clientId !== "") {
				let isExpanded = expandedLabels.includes(item.label);
				if (parentLabel === "Year To Date" && item.label === expandedHomeRemittanceSetupOrSetupItem) {
					isExpanded = true;
				}
				const enhancedItem = {
					...item,
					expanded: item.items ? isExpanded : false,
					items: item.items ? enhanceData(item.items, item.label) : undefined,
					path: item.label === expandedHomeRemittanceSetupOrSetupItem && "/settings/year-to-date/remittance",
				};
				return enhancedItem;
			}
		});
	};

	useEffect(() => {

		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid

		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData || isDeleting}
				testId="remittance"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteEmployeeField}
				cancelActionHandler={onCloseHandler}
				testId="remittance-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

					>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
						>
							<TwoColumnLayout
								toolbar={
									<BasicSetupFormToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
										pageTitle="Remittance"
									/>
								}
								leftColumnContent={
									<>
										<StandardPanel
											title="Remittance"
											testId={`remittance`}
											showTitle={true}
											// variant="outlined"
											topPadding={2}
											borderHide={true}

										>
											<RemittanceYearToDateFieldsLeftPanel
												testId={"remittance-info-panel"}
												yearsDDLItems={yearsDDLItems}
												onYearSelectionChangeHandler={onYearSelectionChangeHandler}
											/>
										</StandardPanel>


									</>

								}
								rightColumnContent={
									<>

										<StandardPanel
											title="Revenu Quebec"
											testId={`revenu-quebec`}
											showTitle={true}
											// variant="outlined"
											topPadding={2}
											borderHide={true}
										>
											<RemittanceYearToDateFieldsRightPanel
												testId={"remittance-info-panel"}
												isAdminUser={isAdminUser}
												selectedSplit={selectedSplit}

											/>
										</StandardPanel>

									</>
								}
								toolbarPosition="top"
								testId="form-layout"

							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"employee-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
